import React, { useState, useEffect, CSSProperties } from "react";
import { Link } from "react-router-dom";
import { cloneDeep } from "lodash";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useAppContext } from "./AppContext";
import {
  accentButtonBackground,
  accentButtonText,
  accentButtonTextWeight,
  accountBoxWidth,
  cardBoxShadow,
  currencyBoxWidth,
  darkModeBorder,
  navBackground,
  navTextColor,
  negativeRed,
  primaryBlue,
  stockYellow,
  tickerBoxWidth,
} from "../util/style";

interface NavProps {
  handleRefreshTradeData: () => void;
  handleRefreshSplitData: () => void;
  handleAddWatchListTicker: (ticker: string) => void;
  handleLogout: () => void;
}

const Nav: React.FC<NavProps> = ({
  handleRefreshTradeData,
  handleRefreshSplitData,
  handleAddWatchListTicker,
  handleLogout,
}) => {
  const {
    autoRefreshClock,
    lastUpdateTime,
    location,
    user,
    isLogged,
    setShowLoginModal,
    tickerListCalled,
    pinInput,
    allTickerString,
    allAccountString,
    searchTickerChart,
    setSearchTickerChart,
    getFont,
    darkMode,
    setDarkMode,
    selectedTicker,
    setSelectedTicker,
    selectedAccount,
    setSelectedAccount,
    selectedCurrency,
    setSelectedCurrency,
    accountInfo,
    tradesByAccount,
    displayDataParsed,
    conversionModes,
    currencyMultiplier,
    pinCode,
    setPinCode,
    privateMode,
    adminMode,
    numFormat,
    handleSearchTicker,
    handlePinCode,
    isRunning,
    serverActive,
    runServiceHealthCheck,
  } = useAppContext() || {};

  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const linkStyle = (path: string): CSSProperties => {
    const isSelected = location?.pathname === path;

    if (isMobile) {
      return {
        border: isSelected ? `2px solid ${primaryBlue}` : "2px solid transparent",
        textDecoration: "none",
        fontSize: getFont?.(14),
        padding: "6px 10px",
        marginRight: "0px",
        marginTop: "4px",
        fontWeight: "600",
        color: "white",
        display: "block",
        marginBottom: "10px",
        borderRadius: "4px",
      };
    }

    return {
      borderBottom: `3px solid ${isSelected ? primaryBlue : "transparent"}`,
      textDecoration: "none",
      fontSize: getFont?.(14),
      padding: "2px 0px",
      marginRight: "10px",
      marginTop: "4px",
      fontWeight: "600",
      color: "white",
      display: "block",
      marginBottom: "0px",
      borderRadius: "0px",
    };
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const accountInfoTyped = accountInfo as Record<string, { name?: string; order?: number }> | undefined;

  return (
    <nav
      style={{
        backgroundColor: navBackground,
        padding: "10px 5px 10px 15px",
        display: "flex",
        gap: "5px",
        alignItems: "center",
        color: navTextColor,
        boxShadow: cardBoxShadow,
        position: "relative",
        flexWrap: isMobile ? "wrap" : "nowrap",
      }}
    >
      {/* Logo */}
      <div
        style={{
          fontSize: getFont?.(20),
          marginRight: "10px",
          fontWeight: "600",
          userSelect: "none",
        }}
      >
        <span style={{ marginRight: "2px", color: "white" }}>Trade</span>
        <span
          style={{
            color: "black",
            padding: "3px 2px",
            borderRadius: "2px",
            backgroundColor: stockYellow,
          }}
        >
          Sapien
        </span>
      </div>
      <div
        style={{
          position: "fixed",
          top: "calc(8px + env(safe-area-inset-top))",
          left: "calc(8px + env(safe-area-inset-left))",
          fontSize: getFont?.(7),
          color: "#888",
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        {autoRefreshClock >= 0 &&
          `${Math.floor(Date.now() / 1000) - (lastUpdateTime?.current || 0)}s`}
      </div>
      {/* Desktop Navigation Links */}
      {!isMobile && (
        <>
          <Link to="/market" style={linkStyle("/market")}>
            Market
          </Link>
          {isLogged && (
            <>
              <Link to="/trades" style={linkStyle("/trades")}>
                Trades
              </Link>
              <Link to="/summary" style={linkStyle("/summary")}>
                Summary
              </Link>
              <Link to="/upload" style={linkStyle("/upload")}>
                Data
              </Link>
            </>
          )}
          <div
            style={{
              fontSize: getFont?.(9),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              padding: "2px 4px",
              lineHeight: "9px",
              borderRadius: "2px",
              cursor: "pointer",
              fontWeight: accentButtonTextWeight,
              color: accentButtonText,
              background: accentButtonBackground,
            }}
            onClick={() => {
              setDarkMode?.((prev: boolean) => !prev);
            }}
          >
            <div>{darkMode ? "Light" : "Dark"}</div>
            <div>Theme</div>
          </div>
        </>
      )}

      {/* Hamburger Menu Button (Mobile) */}
      {isMobile && (
        <div
          style={{
            marginLeft: "auto",
            cursor: "pointer",
            padding: "5px",
          }}
          onClick={toggleMobileMenu}
        >
          {mobileMenuOpen ? (
            <CloseIcon style={{ fontSize: "28px", color: "white" }} />
          ) : (
            <MenuIcon style={{ fontSize: "28px", color: "white" }} />
          )}
        </div>
      )}

      {/* Desktop Right Side Controls */}
      {!isMobile && (
        <>
          <div style={{ marginLeft: "auto" }}></div>
          {isLogged && (
            <>
              <div style={{}}>
                {/* Account Selector */}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{}}>Account</label>
                  <select
                    value={selectedAccount}
                    onChange={(e) => {
                      setSelectedAccount?.(e.target.value);
                    }}
                    style={{
                      background: "rgb(50,50,50)",
                      width: accountBoxWidth + "px",
                      fontSize: getFont?.(10),
                      cursor: "pointer",
                      border: `1px solid ${darkModeBorder}`,
                      color: navTextColor,
                    }}
                  >
                    <option value={allAccountString}>ALL/ACCOUNT</option>
                    {Object.keys(tradesByAccount || {})
                      .sort(
                        (a, b) =>
                          (accountInfoTyped?.[a]?.order || 0) - (accountInfoTyped?.[b]?.order || 0),
                      )
                      .map((account) => (
                        <option key={account} value={account}>
                          {accountInfoTyped?.[account]?.name &&
                            accountInfoTyped?.[account]?.name + " "}
                          {account}
                          &nbsp;&nbsp;$
                          {numFormat?.(
                            (displayDataParsed?.accounts?.[account]?.overallPnL || 0) *
                              (currencyMultiplier || 1),
                            true,
                          )}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div style={{}}>
                {/* Ticker Selector */}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{}}>Traded&nbsp;Ticker</label>
                  <select
                    value={selectedTicker}
                    onChange={(e) => setSelectedTicker?.(e.target.value)}
                    style={{
                      background: "rgb(50,50,50)",
                      width: tickerBoxWidth + "px",
                      fontSize: getFont?.(10),
                      cursor: "pointer",
                      border: `1px solid ${darkModeBorder}`,
                      color: navTextColor,
                    }}
                  >
                    <option value={allTickerString}>ALL/TICKER</option>
                    {selectedAccount === allAccountString &&
                      displayDataParsed?.accounts &&
                      Object.keys(displayDataParsed?.accounts)
                        ?.reduce((acc: string[], eachAccountKey) => {
                          Object.keys(
                            displayDataParsed?.accounts?.[eachAccountKey]
                              ?.trades || {},
                          ).forEach((eachTickerKey) => {
                            if (!acc.includes(eachTickerKey)) {
                              acc.push(eachTickerKey);
                            }
                          });
                          return acc;
                        }, [])
                        .map((ticker) => (
                          <option
                            key={ticker}
                            value={ticker}
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {ticker}&nbsp;&nbsp;$
                            {numFormat?.(
                              (displayDataParsed?.accounts?.[selectedAccount]
                                ?.trades?.[ticker]?.overallPnL || 0) *
                                (currencyMultiplier || 1),
                              true,
                            )}
                          </option>
                        ))}
                    {selectedAccount !== allAccountString &&
                      displayDataParsed?.accounts?.[selectedAccount]?.trades &&
                      Object.keys(
                        cloneDeep(
                          displayDataParsed?.accounts?.[selectedAccount]
                            ?.trades || {},
                        ),
                      )
                        ?.sort((a, b) => a.localeCompare(b))
                        ?.map((ticker) => (
                          <option
                            key={ticker}
                            value={ticker}
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {ticker}&nbsp;&nbsp;$
                            {numFormat?.(
                              (displayDataParsed?.accounts?.[selectedAccount]
                                ?.trades?.[ticker]?.overallPnL || 0) *
                                (currencyMultiplier || 1),
                              true,
                            )}
                          </option>
                        ))}
                  </select>
                </div>
              </div>
            </>
          )}
          <div style={{}}>
            <div>Search&nbsp;Ticker</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: getFont?.(12),
              }}
            >
              <input
                type="text"
                placeholder="(i.e. AAPL)"
                value={searchTickerChart}
                onChange={(e) => {
                  setSearchTickerChart?.(e.target.value?.toUpperCase());
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchTicker?.();
                    handleAddWatchListTicker(searchTickerChart?.toUpperCase() || "");
                  }
                }}
                onClick={() => {
                  setSearchTickerChart?.("");
                }}
                style={{
                  width: "60px",
                  fontSize: getFont?.(10),
                  height: "15px",
                  cursor: "pointer",
                  padding: "1px 3px",
                  background: "rgb(50,50,50)",
                  outline: "none",
                  color: navTextColor,
                  border: `1px solid ${darkModeBorder}`,
                }}
              />
              <div
                onClick={() => {
                  handleSearchTicker?.();
                }}
                style={{
                  marginLeft: "2px",
                  cursor: "pointer",
                  color: accentButtonText,
                  background: accentButtonBackground,
                  padding: "2px 6px",
                  borderRadius: "2px",
                  fontSize: getFont?.(10),
                }}
              >
                <SearchIcon
                  style={{
                    fontSize: getFont?.(7),
                    transform: "scale(1.5) translateY(0px)",
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{}}>Currency</label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency?.(e.target.value)}
              style={{
                background: "rgb(50,50,50)",
                width: currencyBoxWidth + "px",
                fontSize: getFont?.(10),
                cursor: "pointer",
                border: `1px solid ${darkModeBorder}`,
                color: navTextColor,
              }}
            >
              {Object.keys(conversionModes || {}).map((label) => (
                <option key={label} value={label}>
                  {label}
                  {(conversionModes as Record<string, { rate: number }>)?.[label]?.rate !== 1 && (
                    <>
                      /{numFormat?.((conversionModes as Record<string, { rate: number }>)?.[label]?.rate || 0, false, 3)}=USD
                    </>
                  )}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div>Data</div>
            <div
              style={{
                cursor: isRunning ? "" : "pointer",
                fontWeight: accentButtonTextWeight,
                color: accentButtonText,
                background: accentButtonBackground,
                padding: "2px 6px",
                borderRadius: "2px",
                fontSize: getFont?.(10),
                transition: "all 0.15s ease",
                opacity: isRunning ? "0.5" : "",
              }}
              onClick={() => {
                if (isRunning) return;
                if (serverActive?.current) {
                  if (tickerListCalled?.current) {
                    tickerListCalled.current.length = 0;
                  }
                  handleRefreshTradeData();
                  handleRefreshSplitData();
                } else {
                  runServiceHealthCheck?.();
                }
              }}
            >
              Refresh
            </div>
          </div>
          <div style={{ display: isLogged ? "" : "none" }}>
            <div>
              {(adminMode && "Admin") || (privateMode && "Private") || "Hidden"}
              &nbsp;Mode
            </div>
            <div
              onClick={() => {
                setPinCode?.("");
                pinInput?.current?.focus();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                border: `1px solid ${
                  (adminMode && negativeRed) ||
                  (privateMode && primaryBlue) ||
                  "grey"
                }`,
                padding: "1px 2px",
                borderRadius: "2px",
                width: "fit-content",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around",
                  height: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: getFont?.(10),
                    marginRight: "5px",
                    marginLeft: "2px",
                    verticalAlign: "middle",
                  }}
                >
                  PIN:
                </div>
                <input
                  ref={pinInput}
                  onChange={handlePinCode}
                  type="text"
                  autoComplete="off"
                  value={"*".repeat(pinCode?.length || 0)}
                  style={{
                    fontSize: getFont?.(12),
                    background: "transparent",
                    border: `1px solid ${darkModeBorder}`,
                    outline: "none",
                    width: "40px",
                    height: "10px",
                    cursor: "pointer",
                    color: navTextColor,
                  }}
                />
              </div>
            </div>
          </div>
          <div style={{}}>
            {isLogged ? (
              <span style={{}}>User:&nbsp;{user?.username}</span>
            ) : (
              <span>Account</span>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: getFont?.(10),
                color: accentButtonText,
              }}
            >
              <div
                style={{
                  fontSize: getFont?.(10),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "2px 6px",
                  borderRadius: "2px",
                  cursor: "pointer",
                  fontWeight: accentButtonTextWeight,
                  color: accentButtonText,
                  background: accentButtonBackground,
                }}
                onClick={() => {
                  if (isLogged) {
                    handleLogout();
                  } else {
                    setShowLoginModal?.(true);
                  }
                }}
              >
                <div>{isLogged ? "Logout" : "Login"}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Menu Dropdown */}
      {isMobile && mobileMenuOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: navBackground,
            padding: "15px",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {/* Navigation Links */}
          <div style={{ marginBottom: "15px" }}>
            <Link
              to="/market"
              style={linkStyle("/market")}
              onClick={closeMobileMenu}
            >
              Market
            </Link>
            {isLogged && (
              <>
                <Link
                  to="/trades"
                  style={linkStyle("/trades")}
                  onClick={closeMobileMenu}
                >
                  Trades
                </Link>

                <Link
                  to="/upload"
                  style={linkStyle("/upload")}
                  onClick={closeMobileMenu}
                >
                  Data
                </Link>

                <Link
                  to="/summary"
                  style={linkStyle("/summary")}
                  onClick={closeMobileMenu}
                >
                  Summary
                </Link>
              </>
            )}
          </div>

          {/* Theme Toggle */}
          <div
            style={{
              fontSize: getFont?.(12),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: accentButtonTextWeight,
              color: accentButtonText,
              background: accentButtonBackground,
              marginBottom: "15px",
            }}
            onClick={() => {
              setDarkMode?.((prev: boolean) => !prev);
            }}
          >
            {darkMode ? "Light" : "Dark"} Theme
          </div>

          {/* Account Selector */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Account
            </label>
            <select
              value={selectedAccount}
              onChange={(e) => {
                setSelectedAccount?.(e.target.value);
              }}
              style={{
                background: "rgb(50,50,50)",
                width: "100%",
                fontSize: getFont?.(12),
                padding: "8px",
                cursor: "pointer",
                border: `1px solid ${darkModeBorder}`,
                color: navTextColor,
              }}
            >
              <option value={allAccountString}>ALL/ACCOUNT</option>
              {Object.keys(tradesByAccount || {})
                .sort(
                  (a, b) => (accountInfoTyped?.[a]?.order || 0) - (accountInfoTyped?.[b]?.order || 0),
                )
                .map((account) => (
                  <option key={account} value={account}>
                    {accountInfoTyped?.[account]?.name &&
                      accountInfoTyped?.[account]?.name + " "}
                    {account}
                    &nbsp;&nbsp;$
                    {numFormat?.(
                      (displayDataParsed?.accounts?.[account]?.overallPnL || 0) *
                        (currencyMultiplier || 1),
                      true,
                    )}
                  </option>
                ))}
            </select>
          </div>

          {/* Ticker Selector */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Traded Ticker
            </label>
            <select
              value={selectedTicker}
              onChange={(e) => setSelectedTicker?.(e.target.value)}
              style={{
                background: "rgb(50,50,50)",
                width: "100%",
                fontSize: getFont?.(12),
                padding: "8px",
                cursor: "pointer",
                border: `1px solid ${darkModeBorder}`,
                color: navTextColor,
              }}
            >
              <option value={allTickerString}>ALL/TICKER</option>
              {selectedAccount === allAccountString &&
                displayDataParsed?.accounts &&
                Object.keys(displayDataParsed?.accounts)
                  ?.reduce((acc: string[], eachAccountKey) => {
                    Object.keys(
                      displayDataParsed?.accounts?.[eachAccountKey]?.trades || {},
                    ).forEach((eachTickerKey) => {
                      if (!acc.includes(eachTickerKey)) {
                        acc.push(eachTickerKey);
                      }
                    });
                    return acc;
                  }, [])
                  .map((ticker) => (
                    <option key={ticker} value={ticker}>
                      {ticker}
                    </option>
                  ))}
              {selectedAccount !== allAccountString &&
                displayDataParsed?.accounts?.[selectedAccount]?.trades &&
                Object.keys(
                  cloneDeep(
                    displayDataParsed?.accounts?.[selectedAccount]?.trades || {},
                  ),
                )
                  ?.sort((a, b) => a.localeCompare(b))
                  ?.map((ticker) => (
                    <option key={ticker} value={ticker}>
                      {ticker}
                    </option>
                  ))}
            </select>
          </div>

          {/* Search Ticker */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Search Ticker
            </label>
            <div style={{ display: "flex", gap: "5px" }}>
              <input
                type="text"
                placeholder="(i.e. TSLA)"
                value={searchTickerChart}
                onChange={(e) => {
                  setSearchTickerChart?.(e.target.value?.toUpperCase());
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchTicker?.();
                    handleAddWatchListTicker(searchTickerChart?.toUpperCase() || "");
                    closeMobileMenu();
                  }
                }}
                onClick={() => {
                  setSearchTickerChart?.("");
                }}
                style={{
                  flex: 1,
                  fontSize: getFont?.(12),
                  padding: "8px",
                  background: "rgb(50,50,50)",
                  outline: "none",
                  color: navTextColor,
                  border: `1px solid ${darkModeBorder}`,
                }}
              />
              <div
                onClick={() => {
                  handleSearchTicker?.();
                  closeMobileMenu();
                }}
                style={{
                  cursor: "pointer",
                  color: accentButtonText,
                  background: accentButtonBackground,
                  padding: "8px 12px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <SearchIcon style={{ fontSize: "18px" }} />
              </div>
            </div>
          </div>

          {/* Data Refresh */}
          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                cursor: isRunning ? "" : "pointer",
                fontWeight: accentButtonTextWeight,
                color: accentButtonText,
                background: accentButtonBackground,
                padding: "8px 12px",
                borderRadius: "4px",
                fontSize: getFont?.(12),
                transition: "all 0.15s ease",
                opacity: isRunning ? "0.5" : "",
                display: "inline-block",
              }}
              onClick={() => {
                if (isRunning) return;
                if (serverActive?.current) {
                  if (tickerListCalled?.current) {
                    tickerListCalled.current.length = 0;
                  }
                  handleRefreshTradeData();
                  handleRefreshSplitData();
                } else {
                  runServiceHealthCheck?.();
                }
                closeMobileMenu();
              }}
            >
              Refresh Data
            </div>
          </div>

          {/* Currency */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Currency
            </label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency?.(e.target.value)}
              style={{
                background: "rgb(50,50,50)",
                width: "100%",
                fontSize: getFont?.(12),
                padding: "8px",
                cursor: "pointer",
                border: `1px solid ${darkModeBorder}`,
                color: navTextColor,
              }}
            >
              {Object.keys(conversionModes || {}).map((label) => (
                <option key={label} value={label}>
                  {label}
                  {(conversionModes as Record<string, { rate: number }>)?.[label]?.rate !== 1 && (
                    <>
                      /{numFormat?.((conversionModes as Record<string, { rate: number }>)?.[label]?.rate || 0, false, 3)}=USD
                    </>
                  )}
                </option>
              ))}
            </select>
          </div>

          {/* PIN Mode */}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ marginBottom: "5px" }}>
              {(adminMode && "Admin") || (privateMode && "Private") || "Hidden"}{" "}
              Mode
            </div>
            <div
              onClick={() => {
                setPinCode?.("");
                pinInput?.current?.focus();
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                border: `1px solid ${
                  (adminMode && negativeRed) ||
                  (privateMode && primaryBlue) ||
                  "grey"
                }`,
                padding: "8px",
                borderRadius: "4px",
              }}
            >
              <div style={{ fontSize: getFont?.(12), marginRight: "8px" }}>
                PIN:
              </div>
              <input
                ref={pinInput}
                onChange={handlePinCode}
                type="text"
                autoComplete="off"
                value={"*".repeat(pinCode?.length || 0)}
                style={{
                  fontSize: getFont?.(14),
                  background: "transparent",
                  border: `1px solid ${darkModeBorder}`,
                  outline: "none",
                  width: "60px",
                  height: "30px",
                  padding: "4px",
                  cursor: "pointer",
                  color: navTextColor,
                }}
              />
            </div>
          </div>

          {/* Login/Logout */}
          <div>
            {isLogged && (
              <div style={{ marginBottom: "10px" }}>User: {user?.username}</div>
            )}
            <div
              style={{
                fontSize: getFont?.(12),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: accentButtonTextWeight,
                color: accentButtonText,
                background: accentButtonBackground,
              }}
              onClick={() => {
                if (isLogged) {
                  handleLogout();
                } else {
                  setShowLoginModal?.(true);
                }
                closeMobileMenu();
              }}
            >
              {isLogged ? "Logout" : "Login"}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Nav;
